import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { JevStatus } from '@actual-app/core/types/models';

import { useAuth } from '#auth/AuthProvider';
import { Permissions } from '#auth/types';
import { FormField, FormLabel } from '#components/forms';
import { useLoginMethod, useMultiuserEnabled } from '#components/ServerContext';
import { useSyncServerStatus } from '#hooks/useSyncServerStatus';

import { Setting } from './UI';

type TestState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'ok'; model: string }
  | { status: 'error'; message: string };

export function JevSettings() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const loginMethod = useLoginMethod();
  const multiuserEnabled = useMultiuserEnabled();
  const serverStatus = useSyncServerStatus();

  const [jevStatus, setJevStatus] = useState<JevStatus>({});
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [threshold, setThreshold] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [test, setTest] = useState<TestState>({ status: 'idle' });

  async function loadStatus() {
    const status = await send('jev-status');
    setJevStatus(status);
    setModel(status.modelSource === 'secret' ? (status.model ?? '') : '');
    setThreshold(
      status.confidenceThreshold == null
        ? ''
        : String(status.confidenceThreshold),
    );
  }

  useEffect(() => {
    if (serverStatus === 'online') {
      void loadStatus();
    }
  }, [serverStatus]);

  if (serverStatus !== 'online') {
    return null;
  }

  // Same visibility rule as the other server-wide settings
  const isAdmin = hasPermission(Permissions.ADMINISTRATOR);
  const shouldShow = (loginMethod === 'openid' && isAdmin) || !multiuserEnabled;
  if (!shouldShow) {
    return null;
  }

  async function onSave() {
    setIsSaving(true);
    setSaveError(null);
    setTest({ status: 'idle' });

    const updates: { name: string; value: string | null }[] = [];
    if (apiKey.trim()) {
      updates.push({ name: 'openrouter_apiKey', value: apiKey.trim() });
    }
    // An empty model goes back to the environment or default model
    updates.push({ name: 'jev_model', value: model.trim() || null });
    const thresholdValue = Number(threshold);
    if (threshold.trim() && !(thresholdValue >= 0 && thresholdValue <= 1)) {
      setSaveError(t('The confidence threshold must be between 0 and 1'));
      setIsSaving(false);
      return;
    }
    updates.push({
      name: 'jev_confidenceThreshold',
      value: threshold.trim() || null,
    });

    for (const update of updates) {
      const result = await send('secret-set', update);
      if (result && 'error' in result && result.error) {
        setSaveError(result.error);
        setIsSaving(false);
        return;
      }
    }

    setApiKey('');
    await loadStatus();
    setIsSaving(false);
  }

  async function onTest() {
    setTest({ status: 'running' });
    const result = await send('jev-test');
    if (result.ok) {
      setTest({ status: 'ok', model: result.model ?? '' });
    } else {
      setTest({
        status: 'error',
        message: result.details ?? result.reason ?? 'unknown',
      });
    }
  }

  const keyLabel =
    jevStatus.apiKeySource === 'env'
      ? t('Set by the server environment')
      : jevStatus.apiKeySource === 'secret'
        ? t('Saved on the server')
        : t('Not set');

  return (
    <Setting
      primaryAction={
        <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
          <Button variant="primary" onPress={onSave} isDisabled={isSaving}>
            <Trans>Save</Trans>
          </Button>
          <Button
            onPress={onTest}
            isDisabled={!jevStatus.configured || test.status === 'running'}
          >
            <Trans>Test connection</Trans>
          </Button>
        </View>
      }
    >
      <Text>
        <Trans>
          <strong>Automatic classification (JEV)</strong> uses a model from
          OpenRouter to categorize transactions and spot transfers. Only the
          description, amount, date and account type of a transaction are sent.
          The key stays on the server and is never shown again.
        </Trans>
      </Text>
      <FormField>
        <FormLabel title={t('OpenRouter API key')} htmlFor="jev-api-key" />
        <Input
          id="jev-api-key"
          type="password"
          autoComplete="off"
          value={apiKey}
          placeholder={keyLabel}
          onChangeValue={setApiKey}
        />
      </FormField>
      <FormField>
        <FormLabel title={t('Model')} htmlFor="jev-model" />
        <Input
          id="jev-model"
          value={model}
          placeholder={jevStatus.model ?? 'typesafe/jev-1.14'}
          onChangeValue={setModel}
        />
      </FormField>
      <FormField>
        <FormLabel
          title={t('Minimum confidence to apply a category (0 to 1)')}
          htmlFor="jev-threshold"
        />
        <Input
          id="jev-threshold"
          value={threshold}
          placeholder="0.8"
          onChangeValue={setThreshold}
        />
      </FormField>
      {saveError && (
        <Text style={{ color: theme.errorText }}>
          <Trans>Could not save: {{ error: saveError }}</Trans>
        </Text>
      )}
      {test.status === 'ok' && (
        <Text style={{ color: theme.noticeTextLight }}>
          <Trans>Connected using {{ model: test.model }}.</Trans>
        </Text>
      )}
      {test.status === 'error' && (
        <Text style={{ color: theme.errorText }}>
          <Trans>Connection failed: {{ message: test.message }}</Trans>
        </Text>
      )}
    </Setting>
  );
}
